import {
  refreshAccessToken,
  serverRemoveAccessToken,
  serverSetAccessToken,
} from "@/api";
import { trackEvent } from "@/lib/dataLayer";
import { posthog } from "@/lib/posthogClient";
import { useFilter } from "@/services";
import { usePermissionsRevalidation } from "@/hooks/usePermissionsRevalidation";
import { featuresService } from "@/services/featuresService";
import { clearLoginRedirect, consumeLoginRedirect } from "@/utils/loginRedirect";
import { authSession } from "@/utils/authSession";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { authService } from "../../domain/auth/authService";
import type { AuthType, ValidateOtpType } from "../../domain/auth/authType";
import { AuthContext, type AuthContextType } from "./useAuth";

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  usePermissionsRevalidation();
  const [featuresLoaded, setFeaturesLoaded] = useState<boolean>(
    () => featuresService.getFeatures().length > 0
  );
  const { clearAll } = useFilter();

  useEffect(() => {
    if (featuresLoaded) return;

    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");

    if (!authSession.isCookieMode() && (!accessToken || !refreshToken)) {
      setFeaturesLoaded(true);
      return;
    }

    // Mesmo ponto de renovação do interceptor (single-flight + lock entre abas):
    // só dispara rede se o token estiver expirado ou as features ausentes, e
    // nunca desloga no boot — uma sessão inválida será tratada no 1º request.
    refreshAccessToken()
      .catch(() => {})
      .finally(() => {
        setFeaturesLoaded(true);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutate: login, isPending } = useMutation<void, Error, AuthType>({
    mutationFn: async ({ phone, password }) => {
      const { accessToken, refreshToken, features, session } =
        await authService.handleAuth({
          phone,
          password,
        });
      clearAll();
      setToken(accessToken);
      await serverSetAccessToken(accessToken, refreshToken, session);

      if (features) {
        featuresService.setFeatures(features);
      }
    },
    onSuccess: () => {
      trackEvent({ event: "login_success", method: "password" });
      navigate(consumeLoginRedirect() ?? "/dashboard");
    },
    onError: e => {
      toast(e.message, {
        position: "bottom-center",
        autoClose: 5000,
        theme: "light",
        type: "error",
      });
      // Login falhou (senha/telefone errados): não havia sessão pra encerrar,
      // então só limpa qualquer resquício sem usar serverRemoveAccessToken
      // (que dá reload — apagaria os campos do formulário no meio do erro).
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      authSession.clear();
      featuresService.clearFeatures();
      setToken(null);
    },
  });

  const { mutate: loginWithOtp, isPending: isOtpPending } = useMutation<
    void,
    Error,
    ValidateOtpType
  >({
    mutationFn: async ({ phone, code }) => {
      const { accessToken, refreshToken, features, session } =
        await authService.handleValidateOtp({ phone, code });
      clearAll();
      setToken(accessToken);
      await serverSetAccessToken(accessToken, refreshToken, session);

      if (features) {
        featuresService.setFeatures(features);
      }
    },
    onSuccess: () => {
      trackEvent({ event: "login_success", method: "otp" });
      navigate(consumeLoginRedirect() ?? "/dashboard");
    },
    // Erros (código inválido/expirado) são exibidos pela própria tela de OTP,
    // que passa um `onError` na chamada de `loginWithOtp`.
  });

  const { mutate: logout } = useMutation<void, Error, void>({
    mutationFn: async () => {
      // Revoga no servidor antes de limpar o storage. Falha (rede, token já
      // inválido) não pode impedir o logout local.
      await authService.handleLogout().catch(() => undefined);
      // serverRemoveAccessToken reloads the current URL; on a protected page
      // that reload would come back as /auth?redirect=<page>. A chosen logout
      // starts clean (an expired session keeps the redirect on purpose: the
      // same person logs back in and returns to where they were).
      clearLoginRedirect();
      window.history.replaceState(null, "", "/auth");
      await serverRemoveAccessToken("user_logout");
    },
    onSuccess: () => {
      navigate("/auth");
      setToken(null);
      clearAll();
      featuresService.clearFeatures();
      posthog.reset();
    },
  });

  const value: AuthContextType = {
    login,
    loginWithOtp,
    logout,
    isLoading: isPending,
    isOtpLoading: isOtpPending,
    accessToken: token,
    fidexaModules: authSession.getModules(),
    featuresLoaded,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
